import React from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSecurity } from "../hooks/use-security";

// Simple Label component
const Label: React.FC<React.LabelHTMLAttributes<HTMLLabelElement>> = ({
  className,
  children,
  ...props
}) => (
  <label
    className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${
      className || ""
    }`}
    {...props}
  >
    {children}
  </label>
);

export const PasswordSection: React.FC = () => {
  const { t } = useTranslation();

  // Security hook - handles all state and actions
  const {
    currentPassword,
    newPassword,
    confirmPassword,
    showCurrentPassword,
    showNewPassword,
    showConfirmPassword,
    setCurrentPassword,
    setNewPassword,
    setConfirmPassword,
    toggleShowCurrentPassword,
    toggleShowNewPassword,
    toggleShowConfirmPassword,
  } = useSecurity();

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-primary mb-2">
          {t("settings.password.title")}
        </h3>
        <p className="text-sm text-text-tertiary dark:text-text-tertiary">
          {t("settings.password.description")}
        </p>
      </div>

      <div className="space-y-4">
        {/* Current Password */}
        <div className="space-y-2">
          <Label
            htmlFor="currentPassword"
            className="text-sm font-medium text-text-primary dark:text-text-primary"
          >
            {t("settings.password.currentPassword")} *
          </Label>
          <div className="relative">
            <Input
              id="currentPassword"
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="pr-10"
              placeholder="••••••••"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={toggleShowCurrentPassword}
            >
              {showCurrentPassword ? (
                <EyeOff className="h-4 w-4 text-text-tertiary dark:text-text-tertiary" />
              ) : (
                <Eye className="h-4 w-4 text-text-tertiary dark:text-text-tertiary" />
              )}
            </Button>
          </div>
        </div>

        {/* New Password */}
        <div className="space-y-2">
          <Label
            htmlFor="newPassword"
            className="text-sm font-medium text-text-primary dark:text-text-primary"
          >
            {t("settings.password.newPassword")} *
          </Label>
          <div className="relative">
            <Input
              id="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="pr-10"
              placeholder="••••••••"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={toggleShowNewPassword}
            >
              {showNewPassword ? (
                <EyeOff className="h-4 w-4 text-text-tertiary dark:text-text-tertiary" />
              ) : (
                <Eye className="h-4 w-4 text-text-tertiary dark:text-text-tertiary" />
              )}
            </Button>
          </div>
          <p className="text-xs text-text-tertiary dark:text-text-tertiary">
            {t("settings.password.passwordRequirement")}
          </p>
        </div>

        {/* Confirm Password */}
        <div className="space-y-2">
          <Label
            htmlFor="confirmPassword"
            className="text-sm font-medium text-text-primary dark:text-text-primary"
          >
            {t("settings.password.confirmPassword")} *
          </Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pr-10"
              placeholder="••••••••"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={toggleShowConfirmPassword}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4 text-text-tertiary dark:text-text-tertiary" />
              ) : (
                <Eye className="h-4 w-4 text-text-tertiary dark:text-text-tertiary" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
